import { create } from 'zustand';
import { AppState } from 'react-native';

// Local-only callbacks never enter the transcript or the model's tool inputs.
export const useMacAccessRequests = create(() => ({ requests: [], issues: [] }));
export function waitForMacAccess({ userId, taskId, permission, expiresAt, current, authorize, resolveNative, cancelNative, readSystemPermission, reopenSystemSettings, cancelSystemSetup, initialSetup }) {
  return new Promise(resolve => {
    const unavailable = () => Date.now() >= expiresAt
      ? { ok: false, code: 'access-request-expired', error: 'This access request expired. Do not request access again unless the user asks.' }
      : { ok: false, code: 'access-request-dismissed', error: 'This access request is no longer active. Do not request access again unless the user asks.' };
    const unconfirmed = () => ({ ok: false, code: 'access-request-unconfirmed', error: 'Could not confirm the access decision. Do not automatically request permission again.' });
    let finished = false, busy = false, checking = false, timer, systemPermission, browserSetup = false;
    let systemTimer, systemCheck, appStateSubscription, recheckQueued = false, showCheckResult = false;
    const remove = () => useMacAccessRequests.setState(state => ({ requests: state.requests.filter(row => row.id !== permission.token) }));
    const update = patch => {
      if (!finished) useMacAccessRequests.setState(state => ({ requests: state.requests.map(row => row.id === permission.token ? { ...row, ...patch } : row) }));
    };
    const finish = result => {
      if (finished) return;
      finished = true;
      clearInterval(timer); clearTimeout(systemTimer); appStateSubscription?.remove();
      if (result?.ok !== true && cancelNative) {
        Promise.resolve().then(() => cancelNative()).catch(() => {});
      }
      if (systemPermission && result?.ok !== true && cancelSystemSetup) {
        Promise.resolve().then(() => cancelSystemSetup(systemPermission)).catch(() => {});
      }
      remove(); resolve(result);
    };
    const valid = () => current() && Date.now() < expiresAt;
    const checkPermission = (manual = false) => {
      if (finished || !systemPermission) return;
      if (!valid()) return finish(unavailable());
      if (manual) {
        showCheckResult = true;
        update({ rechecking: true, permissionStatus: null, setupError: null });
        // A click during a background probe must get a fresh read afterward.
        if (systemCheck) recheckQueued = true;
      }
      if (systemCheck) return systemCheck;
      clearTimeout(systemTimer);
      systemCheck = (async () => {
        do {
          recheckQueued = false;
          const authorized = await authorize();
          if (finished) return;
          if (!authorized || !valid()) return finish(unavailable());
          const enabled = await readSystemPermission(systemPermission);
          if (finished) return;
          if (!valid()) return finish(unavailable());
          if (enabled === true) return finish({ ok: true });
          update({ setupError: null });
          if (showCheckResult && !recheckQueued) update({ permissionStatus: browserSetup ? 'The extension isn’t connected yet. Finish the browser guide and keep your browser open.' : 'Mac access isn’t available yet. Confirm the switch is on for this app, then quit and reopen Gab AI. If access still fails after installing a different build, remove the old Gab AI entry from this permission list and add the copy you’re running. Then return to this task and check again.' });
        } while (recheckQueued && !finished);
      })().catch(() => {
        if (!valid()) return finish(unavailable());
        update({ setupError: browserSetup ? 'Could not check the browser connection. Try again.' : 'Could not check Mac access. Check your connection and try again.' });
      }).finally(() => {
        systemCheck = null; showCheckResult = false;
        update({ rechecking: false });
        if (!finished) systemTimer = setTimeout(() => checkPermission(), 1000);
      });
      return systemCheck;
    };
    const beginSetup = result => {
      busy = true;
      systemPermission = result.systemPermission;
      browserSetup = result.browserSetup === true;
      update({
        busy: true, waitingForSystem: true, appName: result.appName || permission.appName, browserSetup, permission: browserSetup ? '' : systemPermission,
        systemPermissionName: result.systemPermissionName || permission.systemPermissionName,
        settingsAppName: result.settingsAppName || permission.settingsAppName,
        checkAgain: () => checkPermission(true),
        openSettings: reopenSystemSettings ? async () => {
          try {
            if (!finished && valid() && await authorize() && !finished && valid()) {
              const opened = await reopenSystemSettings(systemPermission);
              if (opened?.ok === false) throw new Error('Settings unavailable');
              update({ setupError: null });
            }
          } catch {
            update({ setupError: browserSetup ? 'Could not reopen the browser guide. Try again.' : 'Could not reopen Mac settings. Try again, or open System Settings from the Apple menu.' });
          }
        } : undefined,
      });
      appStateSubscription = AppState?.addEventListener?.('change', state => {
        if (state === 'active') void checkPermission(true);
      });
      return checkPermission();
    };
    const choose = async approved => {
      if (finished) return;
      if (busy && systemPermission && !approved) return finish({ ok: false, code: 'access-denied', error: 'The user stopped permission setup. Do not ask again unless requested.' });
      if (busy) return;
      busy = true;
      useMacAccessRequests.setState(state => ({ requests: state.requests.map(row => row.id === permission.token ? { ...row, busy: true } : row) }));
      try {
        if (!valid() || !(await authorize()) || !valid()) return finish(unavailable());
        if (finished) return;
        const result = await resolveNative(approved);
        if (finished) return;
        if (result?.ok === true && result.systemPermission && readSystemPermission) return beginSetup(result);
        finish(valid() ? result : unavailable());
      } catch { finish(valid() ? unconfirmed() : unavailable()); }
    };
    timer = setInterval(async () => {
      if (!valid()) return finish(unavailable());
      // A native terminal confirmation can stay open. Keep checking task
      // authorization while it is visible so Stop/expiry can retire it.
      if (checking || finished || (busy && systemPermission)) return;
      checking = true;
      try { if (!(await authorize()) || !valid()) finish(unavailable()); }
      catch { finish(valid() ? unconfirmed() : unavailable()); }
      finally { checking = false; }
    }, 2000);
    useMacAccessRequests.setState(state => ({ requests: [...state.requests, { id: permission.token, userId: String(userId), taskId: String(taskId), title: permission.title, detail: permission.detail, kind: permission.kind, appName: permission.appName, permission: permission.permission, systemPermissionName: permission.systemPermissionName, settingsAppName: permission.settingsAppName, actionLabel: permission.actionLabel, isCurrent: valid, choose, busy: false }] }));
    if (initialSetup?.ok === true && initialSetup.systemPermission && readSystemPermission) void beginSetup(initialSetup);
  });
}
